import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SchemaFormProps {
  schema: any;
  value: any;
  onChange: (value: any) => void;
  errors?: Record<string, string>;
}

// Sync validator shared with the wizard's per-step gate. Keys use the
// `inputData.<key>` prefix so they match the server/fieldErrors keyspace.
export function getSchemaErrors(schema: any, data: any): Record<string, string> {
  const e: Record<string, string> = {};
  (schema?.required ?? []).forEach((k: string) => {
    const v = data?.[k];
    if (v === undefined || v === "" || v === null) e[`inputData.${k}`] = "This field is required";
  });
  return e;
}

export function SchemaForm({ schema, value, onChange, errors }: SchemaFormProps) {
  const [formData, setFormData] = useState(value || {});

  useEffect(() => {
    // Initialize default values or empty strings for required fields if needed
    if (!value && schema?.properties) {
       const initial: any = {};
       Object.keys(schema.properties).forEach(key => {
         initial[key] = "";
       });
       setFormData(initial);
       onChange(initial);
    }
  }, [schema]);

  const handleChange = (key: string, val: any) => {
    const newData = { ...formData, [key]: val };
    setFormData(newData);
    onChange(newData);
  };

  if (!schema || !schema.properties) {
    return <div className="text-muted-foreground text-sm">No specific parameters required for this template.</div>;
  }

  return (
    <div className="space-y-4">
      {Object.entries(schema.properties).map(([key, prop]: [string, any]) => {
        const isRequired = schema.required?.includes(key);
        const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'); // camelCase to Title Case

        return (
          <div key={key} className="grid gap-2">
            <Label htmlFor={key} className="flex items-center gap-1">
              {label}
              {isRequired && <span className="text-red-500">*</span>}
            </Label>
            
            {prop.enum ? (
              <Select 
                value={formData[key] || ""} 
                onValueChange={(v) => handleChange(key, v)}
              >
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {prop.enum.map((option: string) => (
                    <SelectItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : prop.type === 'number' || prop.type === 'integer' ? (
               <Input
                id={key}
                type="number"
                placeholder={prop.description || `Enter ${label.toLowerCase()}`}
                className="bg-background/50"
                value={formData[key] || ""}
                onChange={(e) => handleChange(key, e.target.value === "" ? "" : Number(e.target.value))}
              />
            ) : key === 'description' || key === 'requirements' || prop.maxLength > 100 ? (
               <Textarea
                id={key}
                placeholder={prop.description || `Enter ${label.toLowerCase()}`}
                className="bg-background/50 min-h-[100px]"
                value={formData[key] || ""}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            ) : (
              <Input
                id={key}
                type="text"
                placeholder={prop.description || `Enter ${label.toLowerCase()}`}
                className="bg-background/50"
                value={formData[key] || ""}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            )}
            {prop.description && <p className="text-xs text-muted-foreground">{prop.description}</p>}
            {errors?.[`inputData.${key}`] && (
              <p className="text-xs text-destructive">{errors[`inputData.${key}`]}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
